import { useState, useCallback, ReactNode } from 'react';
import styled from 'styled-components';
import { useUserContext } from 'contexts/UserContext';

import type {
  ServicePartner,
  SectionView,
} from 'components/PageWrappers/AdDetails/types';
import type { NextLinkProps } from 'types';

import { WhitePanel } from 'components/Toolkit/WhitePanel/WhitePanel';
import { InfoTitle } from 'components/Toolkit/InfoTitle/InfoTitle';
import { KeyInfoList } from 'components/Toolkit/KeyInfoList/KeyInfoList';
import { KeyInfoCard } from 'components/Toolkit/KeyInfoCard/KeyInfoCard';
import { OptionalNextLink } from 'components/Toolkit/OptionalNextLink/OptionalNextLink';
import { SimpleShowMore } from 'components/Toolkit/SimpleShowMore/SimpleShowMore';
import { Notification } from 'components/Toolkit/Notification/Notification';
import { SeeMoreFade } from 'components/Toolkit/SeeMoreFade/SeeMoreFade';
import { media } from 'helpers/breakpoints';
import { pushToDataLayer } from 'services/gtmService';

import type { IAdAttributeView } from 'types';
import type { MappedVehicleFeatures } from 'components/PageWrappers/AdDetails/components/VehicleFeatures/VehicleFeatures.typed';
import VehicleFeaturesShowMore from 'components/PageWrappers/AdDetails/components/VehicleFeatures/VehicleFeaturesShowMore';
export interface InfoPanelProps {
  title: string;
  listItems?: string[];
  information: string;
  renderAction?: ReactNode;
  displayAttributes?: IAdAttributeView[];
  cdnUrl?: string;
  servicePartners?: ServicePartner[];
  Link?: NextLinkProps;
  section?: SectionView;
  renderDfpUnit?: ReactNode;
  isPreview?: boolean;
  className?: string;
  vehicleFeatures?: MappedVehicleFeatures[];
}

const SInfoTitle = styled(InfoTitle)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  @media (min-width: 840px) {
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const StyledText = styled.p`
  font-family: inherit;
  white-space: pre-line;
`;

const SSimpleShowMore = styled(SimpleShowMore)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${media.large} {
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const ReportAd = styled.div`
  margin-top: ${({ theme }) => theme.spacing.M16};
`;

const KeyInfoListContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.M24};
`;

const DFP = styled.div<{ margin: 'top' | 'bottom' }>`
  padding: ${({ theme }) => `${theme.spacing.M16} 0`};
  border-top: ${({ theme }) => `1px solid ${theme.colors.GREY_LIGHT}`};
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.GREY_LIGHT}`};
  overflow: hidden;

  /* Adjust the height of the placement to avoid CLS issues */
  height: 138px;
  @media screen and (min-width: 704px) {
    height: 82px;
  }

  @media screen and (min-width: 1024px) {
    height: 138px;
  }

  @media screen and (min-width: 1080px) {
    height: 82px;
  }

  /* Adjust margin depending on where unit is within component */
  ${({ theme, margin }) =>
    margin === 'top'
      ? `
    margin-top: ${theme.spacing.M16};
    ${media.large} {
      margin-top: ${theme.spacing.M24};
    }
  `
      : `
    margin-bottom: ${theme.spacing.M16};
    ${media.large} {
      margin-bottom: ${theme.spacing.M24};
    }
  `}
`;

const ServicePartnerWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.OFFWHITE};
  width: ${({ theme }) => `calc(100% + ${theme.spacing.M32})`};
  padding: ${({ theme }) => theme.spacing.M16};
  margin-left: -${({ theme }) => theme.spacing.M16};
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${media.large} {
    width: ${({ theme }) => `calc(100% + ${theme.spacing.L48})`};
    padding: ${({ theme }) => theme.spacing.M24};
    margin-left: -${({ theme }) => theme.spacing.M24};
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const ServicePartnerTitle = styled.p`
  ${({ theme }) => theme.fontSize.L18};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const ServicePartnerContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: ${({ theme }) => theme.spacing.S8};

  ${media.medium} {
    grid-template-columns: repeat(2, 1fr);
    row-gap: ${({ theme }) => theme.spacing.M16};
    column-gap: ${({ theme }) => theme.spacing.M16};
  }
`;

const VehicleDetailsNotification = styled(Notification)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${media.large} {
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

function InfoPanel({
  title,
  listItems,
  information,
  renderAction,
  cdnUrl,
  section,
  displayAttributes,
  servicePartners,
  Link,
  renderDfpUnit,
  isPreview = false,
  className,
  vehicleFeatures,
}: InfoPanelProps) {
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);
  const [
    isVehicleFeaturesContentOverflowing,
    setIsVehicleFeaturesContentOverflowing,
  ] = useState(false);

  const shouldRenderDfpUnitAtBottom = !Boolean(
    displayAttributes && displayAttributes?.length > 0,
  );
  const { setIsHistoryCheckModalOpen } = useUserContext();

  const measuredRef = useCallback((element: HTMLDivElement) => {
    if (element !== null) {
      setIsContentOverflowing(element.offsetHeight > 166);
    }
  }, []);

  const vehicleFeaturesRef = useCallback((element: HTMLDivElement) => {
    if (element !== null) {
      setIsVehicleFeaturesContentOverflowing(element.offsetHeight > 166);
    }
  }, []);

  function getIllustration(name?: string) {
    switch (name) {
      case 'historyCheck':
        return `${cdnUrl}/images/illustrations/history-check.svg`;
      case 'insurance':
        return `${cdnUrl}/images/illustrations/insurance.svg`;
      case 'contract':
        return `${cdnUrl}/images/illustrations/contract.svg`;
      case 'autoguru':
        return `${cdnUrl}/images/illustrations/auto-guru.svg`;
      case 'prepurchaseCheck':
        return `${cdnUrl}/images/illustrations/partner-prepurchase-check.svg`;
      default:
        return undefined;
    }
  }

  function handleKeyInfoCardClick(title: string) {
    if (title === 'historyCheck') {
      setIsHistoryCheckModalOpen(true);
    }

    if (servicePartners) {
      const partner = servicePartners.filter(
        (partner) => partner.name === title,
      )[0];

      pushToDataLayer({
        event: 'partner_clicks',
        servicePartnerType: partner.link?.partnerType,
        servicePartnerName: partner.link?.partnerName,
      });
    }
  }

  return (
    <WhitePanel className={className}>
      {displayAttributes && displayAttributes?.length > 0 && (
        <>
          <KeyInfoListContainer>
            <SInfoTitle title="Key Info" listItems={['Overview']} />
            <KeyInfoList infoList={displayAttributes} cdnUrl={cdnUrl} />
          </KeyInfoListContainer>
          {isPreview && section?.name !== 'beefcattle' && (
            <VehicleDetailsNotification
              type="PLAIN"
              text="More details about your vehicle may be automatically displayed in your ad"
              hideIcon
            />
          )}
        </>
      )}
      {servicePartners && (
        <ServicePartnerWrapper>
          <ServicePartnerTitle>Before you buy</ServicePartnerTitle>
          <ServicePartnerContainer>
            {servicePartners.map((servicePartner) => {
              return (
                <OptionalNextLink
                  key={servicePartner.title}
                  Link={Link}
                  href={
                    servicePartner.name !== 'historyCheck'
                      ? servicePartner?.link?.action ??
                        servicePartner?.link?.url
                      : undefined
                  }
                  isExternal={servicePartner.link?.external}
                >
                  <KeyInfoCard
                    title={servicePartner.title}
                    illustration={getIllustration(servicePartner.name)}
                    linkText={servicePartner.link?.title}
                    isClickable={true}
                    onCardClick={handleKeyInfoCardClick}
                    sponsoredIllustration={servicePartner.iconPoweredBy}
                    sponsoredIllustrationText="Powered by"
                    identifier={servicePartner.name}
                    className={`${servicePartner.link?.partnerName}-${servicePartner.link?.partnerType}-imp`}
                  />
                </OptionalNextLink>
              );
            })}
          </ServicePartnerContainer>
        </ServicePartnerWrapper>
      )}

      {section &&
        (section.name === 'cars' || section.name === 'commercials') && (
          <SSimpleShowMore
            visibleText={
              'All information is provided by third parties and DoneDeal are not in a position to offer any warranty or guarantee in relation to the content.'
            }
            hiddenText={
              'Both buyer and seller should confirm all information in relation to the vehicle before committing to the sale. We disclaim all liability arising out of or in connection with any reliance placed on the above content. We accept no responsibility for keeping the content and information accurate, up to date or complete.'
            }
          />
        )}

      {!shouldRenderDfpUnitAtBottom && renderDfpUnit && (
        <DFP margin="bottom">{renderDfpUnit}</DFP>
      )}

      <SInfoTitle title={title} listItems={listItems} />
      <SeeMoreFade
        mobileContainerMaxHeight={166}
        isContentOverflowing={isContentOverflowing}
      >
        <StyledText ref={measuredRef}>{information}</StyledText>
      </SeeMoreFade>

      {vehicleFeatures && (
        <VehicleFeaturesShowMore
          isContentOverflowing={isVehicleFeaturesContentOverflowing}
          vehicleFeatures={vehicleFeatures}
          ref={vehicleFeaturesRef}
        />
      )}

      {shouldRenderDfpUnitAtBottom && renderDfpUnit && (
        <DFP margin="top">{renderDfpUnit}</DFP>
      )}
      {renderAction && <ReportAd>{renderAction}</ReportAd>}
    </WhitePanel>
  );
}

export { InfoPanel };
